import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import NewsNavbar from '@/components/feature/NewsNavbar';
import NewsFooter from '@/components/feature/NewsFooter';
import NewsCard from '@/components/feature/NewsCard';
import { supabase, type NewsItem } from '@/lib/supabase';
import { useReadingList } from '@/hooks/useReadingList';

export default function ReadingListPage() {
  const { savedIds, removeAll } = useReadingList();
  const [confirmClear, setConfirmClear] = useState(false);
  const [savedNews, setSavedNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (savedIds.length === 0) { setSavedNews([]); setLoading(false); return; }
    supabase.from('news').select('*').in('id', savedIds).then(({ data }) => {
      setSavedNews(data || []);
      setLoading(false);
    });
  }, [savedIds]);

  const handleClearAll = () => {
    if (confirmClear) {
      removeAll();
      setConfirmClear(false);
    } else {
      setConfirmClear(true);
      setTimeout(() => setConfirmClear(false), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E27] font-sans">
      <NewsNavbar />
      <main className="pt-28 md:pt-32">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-white/40 mb-4">
            <Link to="/" className="hover:text-white transition-colors cursor-pointer">Ana Sayfa</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-white">Okuma Listesi</span>
          </nav>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center bg-[#00D9FF]/10 rounded-xl">
                <i className="ri-bookmark-line text-[#00D9FF] text-2xl" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-black text-white">Okuma Listesi</h1>
                <p className="text-white/50 text-sm">
                  {savedNews.length > 0 ? `${savedNews.length} kayıtlı haber` : 'Henüz haber kaydedilmedi'}
                </p>
              </div>
            </div>
            {savedNews.length > 0 && (
              <button
                onClick={handleClearAll}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  confirmClear
                    ? 'bg-red-500/20 border border-red-500/50 text-red-400'
                    : 'bg-white/5 border border-white/10 text-white/60 hover:text-white hover:bg-white/10'
                }`}
              >
                <i className={`${confirmClear ? 'ri-alert-line' : 'ri-delete-bin-line'} text-sm`} />
                {confirmClear ? 'Emin misiniz? Tekrar tıklayın' : 'Tümünü Temizle'}
              </button>
            )}
          </div>

          {loading ? (
            <div className="text-center py-16 text-white/40">
              <i className="ri-loader-4-line text-3xl animate-spin block mb-3" />Yükleniyor...
            </div>
          ) : savedNews.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pb-16">
              {savedNews.map(item => <NewsCard key={item.id} item={item} />)}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-24 h-24 flex items-center justify-center bg-white/5 rounded-full mb-6">
                <i className="ri-bookmark-line text-5xl text-white/20" />
              </div>
              <h2 className="text-white font-bold text-xl mb-3">Okuma Listeniz Boş</h2>
              <p className="text-white/50 text-base max-w-sm mb-8">
                Haberlerin üzerindeki
                <i className="ri-bookmark-line mx-1 text-[#00D9FF]" />
                ikonuna tıklayarak haberleri okuma listenize ekleyebilirsiniz.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#00D9FF] text-[#0A0E27] font-bold rounded-full hover:bg-[#00D9FF]/90 transition-all cursor-pointer"
              >
                <i className="ri-home-line" />Haberlere Göz At
              </Link>
            </div>
          )}
        </div>
      </main>
      <NewsFooter />
    </div>
  );
}
